import { create } from 'zustand';

export type Locale = 'ru' | 'en';

export const LOCALES: Array<{ code: Locale; label: string }> = [
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
];

const STORAGE_KEY = 'heritage.locale';

function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'ru' || stored === 'en') return stored;
    const browserLanguage = (
      navigator.language ||
      navigator.languages?.[0] ||
      ''
    ).toLowerCase();
    if (browserLanguage.startsWith('en')) {
      return 'en';
    }
  } catch {
    // localStorage can be unavailable.
  }
  return 'ru';
}

const EN: Record<string, string> = {
  // Shell / common
  'Heritage': 'Heritage',
  'Семья P&Co': 'Family P&Co',
  'Моя семья': 'My family',
  'Семья P&Co — семейное наследие': 'Family P&Co — family heritage',
  'Главная': 'Home',
  'Древо': 'Tree',
  'Чаты': 'Chats',
  'Дневник': 'Diary',
  'События': 'Events',
  'Пространства': 'Spaces',
  'Уведомления': 'Notifications',
  'Профиль': 'Profile',
  'Настройки': 'Settings',
  'Тема': 'Theme',
  'Оформление приложения': 'App appearance',
  'Светлая': 'Light',
  'Тёмная': 'Dark',
  'Системная': 'System',
  'Светлая тема': 'Light theme',
  'Тёмная тема': 'Dark theme',
  'Назад': 'Back',
  'Отмена': 'Cancel',
  'Сохранить': 'Save',
  'Удалить': 'Delete',
  'Редактировать': 'Edit',
  'Создать': 'Create',
  'Добавить': 'Add',
  'Отправить': 'Send',
  'Скрыть': 'Hide',
  'Состав': 'Members',
  'Цвет': 'Color',
  'Название': 'Name',
  'Описание': 'Description',
  'Место': 'Place',
  'Дата': 'Date',
  'Значение': 'Value',
  'Загрузка': 'Loading',
  'Загрузка…': 'Loading…',
  'Синхронизировать': 'Sync',
  'Нет сети': 'No connection',
  'Нет соединения': 'No connection',
  'Оффлайн-режим. Изменения сохраняются на устройстве.':
    'Offline mode. Changes are saved on your device.',
  'Ожидают отправки: {count}': '{count} pending',
  'Конфликтов синхронизации: {count}': '{count} sync conflicts',
  'Ожидает синхронизации': 'Pending sync',
  'ожидает': 'pending',
  'ожидает синхронизации': 'pending sync',
  'Проверяем приглашение…': 'Checking invitation…',
  'Работа без сети': 'Offline mode',
  'Установить на устройство': 'Install app',
  'Скачать Android APK': 'Download Android APK',

  // Auth / login
  'Закрытое пространство вашей семьи': "Your family's private space",
  'Войти': 'Sign in',
  'Вход…': 'Signing in…',
  'Пароль': 'Password',
  'Email': 'Email',
  'Email или логин': 'Email or login',
  'you@example.com или ivan.petrov': 'you@example.com or ivan.petrov',
  'Логин': 'Login',
  '(необязательно)': '(optional)',
  'Только латиница, цифры, точка, дефис или подчёркивание. Можно входить по логину или email.':
    'Latin letters, digits, dot, hyphen or underscore only. You can sign in with login or email.',
  'Логин должен содержать 3–32 символа: латиница, цифры, точка, дефис или подчёркивание.':
    'Login must contain 3–32 characters: Latin letters, digits, dot, hyphen or underscore.',
  'Логин уже занят': 'Login is already taken',
  'Укажите логин или email': 'Enter a login or email',
  'Входить можно по логину или email.': 'You can sign in with login or email.',
  'Email не указан. Вход возможен только по логину.':
    'Email is not set. Sign-in is possible only with a login.',
  'Сохранить логин': 'Save login',
  'Логин сохранён.': 'Login saved.',
  'Не удалось сохранить логин': 'Could not save login',
  'Только латиница, цифры, точка, дефис или подчёркивание. Email можно использовать для входа как раньше.':
    'Latin letters, digits, dot, hyphen or underscore only. Email can still be used to sign in.',
  'Без email': 'No email',
  'Пользователь': 'User',
  'you@example.com': 'you@example.com',
  'Регистрация доступна только по приглашению. Попросите ссылку у администратора семьи.':
    'Registration is invite-only. Ask a family admin for an invite link.',
  'Есть ссылка-приглашение? Откройте её в браузере — форма появится автоматически.':
    'Have an invite link? Open it in your browser and the form appears automatically.',
  'Приглашение в семью': 'Family invitation',
  'Приглашение не найдено': 'Invitation not found',
  'Ссылка недействительна или была отозвана. Попросите администратора семьи создать новую.':
    'The link is invalid or was revoked. Ask a family admin to create a new one.',
  'Приглашение больше не действует.': 'This invitation is no longer valid.',
  'Приглашение выдано на {email}': 'Invitation issued for {email}',
  'Присоединиться': 'Join',
  'Создаём аккаунт…': 'Creating account…',
  'Минимум 8 символов': 'At least 8 characters',
  'Имя': 'First name',
  'Фамилия': 'Last name',
  'Не удалось войти': 'Sign in failed',
  'Не удалось завершить регистрацию': 'Registration failed',
  'Неверный пароль': 'Incorrect password',
  'Неверный текущий пароль': 'Current password is incorrect',
  'Новый пароль совпадает с текущим': 'The new password matches the current password',
  'Email уже занят': 'Email is already in use',
  'Нельзя пригласить второго владельца семьи':
    'You cannot invite a second family owner',
  'Это уже ваш текущий email': 'This is already your current email',
  'Недостаточно прав для этого пространства': 'Insufficient permissions for this space',
  'Нет доступа к этой персоне': 'You do not have access to this person',
  'Персона не найдена': 'Person not found',
  'Персона не найдена в этой семье': 'Person not found in this family',
  'Одна или несколько персон не найдены': 'One or more people were not found',
  'Одна или несколько персон не найдены в этой семье':
    'One or more people were not found in this family',
  'Персона для доступа не найдена': 'Person for access was not found',
  'Пространство не найдено': 'Space not found',
  'Некорректное пространство': 'Invalid space',
  'Семейное пространство не найдено': 'Family space not found',
  'Участник семьи не найден': 'Family member not found',
  'Сообщение не найдено': 'Message not found',
  'Синхронизация не найдена': 'Sync not found',
  'Файл не найден': 'File not found',
  'Файл не найден в этой семье': 'File not found in this family',
  'Файл не найден на диске': 'File not found on disk',
  'Файл не передан': 'No file provided',
  'Файл слишком большой': 'File is too large',
  'Этот тип файла не поддерживается': 'This file type is not supported',
  'Некорректный JSON': 'Invalid JSON',
  'Некорректный OAuth state': 'Invalid OAuth state',
  'OAuth code/state отсутствует': 'OAuth code or state is missing',
  'OAuth state истёк': 'OAuth state has expired',
  'Gmail OAuth не настроен': 'Gmail OAuth is not configured',
  'Gmail не подключён': 'Gmail is not connected',
  'Google не выдал refresh token': 'Google did not return a refresh token',
  'Укажите период поиска писем': 'Specify the email search period',
  'Укажите персону для импорта': 'Select a person for import',
  'Начало периода позже конца': 'The start of the period is after the end',
  'Один или несколько файлов не найдены в этой семье':
    'One or more files were not found in this family',
  'Некоторые записи не найдены': 'Some entries were not found',
  'Строка без имени пропущена': 'A row without a name was skipped',
  'CSV пуст': 'The CSV file is empty',
  'В файле GEDCOM не найдено персон': 'No people were found in the GEDCOM file',
  'В JSON не найдено персон': 'No people were found in the JSON file',
  'Можно изменять только свои записи': 'You can only edit your own entries',
  'Можно редактировать только свои сообщения': 'You can only edit your own messages',
  'Можно удалять только свои сообщения': 'You can only delete your own messages',
  'Участников можно добавлять только в групповой чат':
    'Members can only be added to a group chat',
  'Выберите семейные пространства или персон для доступа':
    'Choose family spaces or people for access',

  // Dashboard
  'Добро пожаловать': 'Welcome',
  'Создайте семейное пространство или откройте приглашение от родственника.':
    'Create a family space or open an invitation from a relative.',
  'Новая семья': 'New family',
  'Например, семья Ивановых': 'For example, the Ivanov family',
  'Создать семью': 'Create family',
  'Создаём…': 'Creating…',
  'Не удалось создать семью': 'Could not create family',
  'Семейное пространство': 'Family space',
  'Добавить персону': 'Add person',
  'Недавно обновлённые персоны': 'Recently updated people',
  'Всё древо': 'Full tree',
  'Показать всё': 'Show all',
  'Ближайшие события': 'Upcoming events',
  'Все события': 'All events',
  'Ближайших событий нет.': 'No upcoming events.',
  'Последние события': 'Recent events',
  'В древе пока нет персон.': 'There are no people in the tree yet.',
  'Персон': 'People',
  'Связей': 'Relationships',
  'Событий': 'Events',
  'Непрочитанных': 'Unread',
  'Непрочитанных сообщений: {count}': 'Unread messages: {count}',
  'Переименовать': 'Rename',
  'Название семьи': 'Family name',
  'Персоны, связи и факты': 'People, relationships and facts',
  'Личные и семейные беседы': 'Personal and family conversations',
  'Истории и воспоминания': 'Stories and memories',
  'Записи, факты и воспоминания': 'Entries, facts and memories',
  'Встречи и памятные даты': 'Gatherings and memorable dates',
  'Ветки внутри общего древа': 'Branches within the shared tree',
  'Ближайшие родственники': 'Close relatives',
  'Ближайшие родственники не найдены.': 'No close relatives found.',
  'Персоны, связи и дневник': 'People, relationships and diary',
  'Аккаунты и привязка к персонам': 'Accounts and person links',
  'Где живут родственники': 'Where relatives live',

  // Tree
  'На главную': 'Home',
  'Семейное древо': 'Family tree',
  'Фокус': 'Focus',
  'Фокус на персоне': 'Focus on person',
  'Пространство': 'Space',
  'Показать всё древо': 'Show full tree',
  'Свернуть ветку': 'Collapse branch',
  'Развернуть ветку': 'Expand branch',
  'Развернуть все': 'Expand all',
  'Выберите персону в списке или нажмите на неё в древе, чтобы сфокусироваться на близких родственниках. Можно выбрать семейное пространство, чтобы показать только его ветку.':
    'Pick a person from the list or tap them in the tree to focus on close relatives. You can also pick a family space to show only that branch.',
  'Показаны родители, братья и сёстры, супруги и дети:':
    'Showing parents, siblings, spouses and children:',
  'Открыть профиль →': 'Open profile →',
  'Родители': 'Parents',
  'Братья и сёстры': 'Siblings',
  'Супруги': 'Spouses',
  'Дети': 'Children',
  'Просмотреть в древе': 'View in tree',
  'Открыть страницу': 'Open page',
  'Написать сообщение': 'Send message',
  'Нет аккаунта для сообщения': 'No account for messages',
  'Добавить сведения': 'Add details',
  'День рождения': 'Birthday',
  'сегодня': 'today',
  'через {days} дн.': 'in {days} days',
  'исполняется {age}': 'turns {age}',
  'Пространство {name}: {members} персон. Связанных родственников вне пространства: {context} — они показаны бледно, чтобы не терять связь с общим древом.':
    'Space {name}: {members} people. Related people outside the space: {context} — shown faded to keep the connection to the shared tree.',
  'Связать персон': 'Link people',
  'Родитель → ребёнок': 'Parent → child',
  'Супруг(а)': 'Spouse',
  'Партнёр': 'Partner',
  'Выберите партнёра': 'Select a partner',
  'Для брака или развода выберите партнёра.':
    'Select a partner for a marriage or divorce entry.',
  'Связать': 'Link',
  'Импорт и экспорт': 'Import and export',
  'GEDCOM (.ged), JSON для резервной копии и CSV со списком персон.':
    'GEDCOM (.ged), JSON for backups and CSV with a list of people.',
  'Импортировать файл': 'Import file',
  'Добавлено персон: {persons}, связей: {relationships}, пропущено: {skipped}':
    'People added: {persons}, relationships: {relationships}, skipped: {skipped}',
  'Удалить персону': 'Delete person',
  'Удалить персону «{name}»? Вместе с ней удалятся все её связи, записи дневника и отметки.':
    'Delete the person “{name}”? All their relationships, diary entries and mentions will also be deleted.',
  'Действие необратимо: связи, факты и отметки персоны тоже удалятся.':
    'This cannot be undone: the person’s relationships, facts and mentions are deleted too.',
  'Выберите персону': 'Select a person',
  'Год рождения': 'Year of birth',
  'Пол не указан': 'Gender not specified',
  'Мужской': 'Male',
  'Женский': 'Female',
  'Другой': 'Other',
  'Кто': 'From',
  'Кому': 'To',
  'Персона сохранена на устройстве и будет добавлена при подключении.':
    'Person saved on device and will be added when online.',
  'Связь сохранена на устройстве и будет добавлена при подключении.':
    'Relationship saved on device and will be added when online.',
  'Персона удалена.': 'Person deleted.',
  'Эта персона ещё не синхронизирована с сервером.':
    'This person is not synced with the server yet.',
  'Не удалось добавить персону': 'Could not add person',
  'Не удалось создать связь': 'Could not create relationship',
  'Не удалось удалить персону': 'Could not delete person',
  'Импорт не удался. Проверьте формат файла.':
    'Import failed. Check the file format.',
  'Экспорт не удался': 'Export failed',
  'Пока нет персон — добавьте первую.': 'No people yet — add the first one.',
  'Древо пустое — добавьте первую персону.':
    'The tree is empty — add the first person.',
  'Семейное фото': 'Family photo',
  'Фото или документ': 'Photo or document',
  'Прикреплено: {name}': 'Attached: {name}',
  'Загрузка фото и документов требует подключения к сети.':
    'Uploading photos and documents requires a connection.',
  'Отправка фото и документов требует подключения к сети.':
    'Sending photos and documents requires a connection.',
  'Не удалось загрузить файл': 'Could not upload file',
  'Документ · {size} КБ': 'Document · {size} KB',
  'Загрузка вложений…': 'Loading attachments…',
  'Фото': 'Photo',
  'Документ Семьи P&Co': 'Family P&Co document',

  // Person
  'К древу': 'To tree',
  'Показать в древе': 'Show in tree',
  'События из профиля': 'Profile events',
  'Из профиля': 'From profile',
  'Сведений о датах пока нет': 'No date information yet',
  'Биография, воспоминания, заметки': 'Biography, memories, notes',
  'Биография': 'Biography',
  'Факты': 'Facts',
  'Добавить факт': 'Add fact',
  'на проверке': 'pending review',
  'Без описания': 'No description',
  'Источник: {note}': 'Source: {note}',
  'Фактов пока нет.': 'No facts yet.',
  'Источник сведений': 'Source of information',
  'Связи не загружены на устройстве.': 'Relationships are not loaded on this device.',
  'Отчество': 'Middle name',
  'Девичья фамилия': 'Maiden name',
  'Дата рождения': 'Date of birth',
  'Место рождения': 'Place of birth',
  'Дата смерти': 'Date of death',
  'Место смерти': 'Place of death',
  'Рождение': 'Birth',
  'Смерть': 'Death',
  'Только ссылка': 'Link only',
  'Отправить приглашение': 'Send invitation',
  'Введите email, на который отправить приглашение.': 'Enter the email address to send the invitation to.',
  'Приглашение по email': 'Email invitation',
  'Ссылка создана, но письмо не удалось отправить. Скопируйте ссылку вручную.': 'The link was created, but the email could not be sent. Copy the link manually.',
  'Приглашение отправлено на {email}.': 'Invitation sent to {email}.',
  'Укажите email для приглашения.': 'Enter an email for the invitation.',
  'Выберите персону в фильтре сверху.': 'Choose a person in the top filter.',
  'Пространство по умолчанию': 'Default space',
  'Активное пространство': 'Active space',
  'Все пространства': 'All spaces',
  'брат/сестра супруга': 'spouse’s sibling',
  'золовка/свояченица': 'sister-in-law',
  'шурин/свояк': 'brother-in-law',
  'супруг(а) брата/сестры': 'sibling’s spouse',
  'невестка/свояченица': 'sister-in-law',
  'свояк/зять': 'brother-in-law',
  'супруг(а) ребёнка': 'child’s spouse',
  'сноха/невестка': 'daughter-in-law',
  'зять': 'son-in-law',
  'родитель супруга': 'spouse’s parent',
  'свекровь/тёща': 'mother-in-law',
  'свёкор/тесть': 'father-in-law',
  'троюродная сестра': 'second cousin (female)',
  'троюродный брат': 'second cousin (male)',
  'двоюродная бабушка': 'great-aunt',
  'двоюродный дедушка': 'great-uncle',
  'правнучка': 'great-granddaughter',
  'правнук': 'great-grandson',
  'прабабушка': 'great-grandmother',
  'прадедушка': 'great-grandfather',
  'Троюродные': 'Second cousins',
  'Двоюродные бабушки': 'Great-aunts',
  'Двоюродные дедушки': 'Great-uncles',
  'Правнуки': 'Great-grandchildren',
  'Прадедушки и прабабушки': 'Great-grandparents',
  'Братья и сёстры супруга': 'Spouse’s siblings',
  'Супруги братьев и сестёр': 'Siblings-in-law',
  'Снохи и зяти': 'Children-in-law',
  'Свекры': 'Parents-in-law',
  'Отчим и мачеха': 'Step-parents',
  'Пасынки и падчерицы': 'Stepchildren',
  'Единокровные и единоутробные': 'Half-siblings',
  'Близнецы': 'Twins',
  'Сводные братья и сёстры': 'Step-siblings',
  'Партнёры': 'Partners',
  'Бывшие супруги': 'Former spouses',
  'Бывшие партнёры': 'Former partners',
  'Прапрадедушки и прапрабабушки': 'Great-great-grandparents',
  'Дедушки и бабушки супруга': 'Spouse’s grandparents',
  'Праправнуки': 'Great-great-grandchildren',
  'Внуки супруга': 'Spouse’s grandchildren',
  'Супруги дядей': 'Uncles’ spouses',
  'Супруги тёть': 'Aunts’ spouses',
  'Дяди супруга': 'Spouse’s uncles',
  'Тёти супруга': 'Spouse’s aunts',
  'Двоюродные прадедушки': 'Great-great-uncles',
  'Двоюродные прабабушки': 'Great-great-aunts',
  'Супруги племянников': 'Nephews’ and nieces’ spouses',
  'Внучатые племянники': 'Grand-nephews and grand-nieces',
  'Племянники супруга': 'Spouse’s nephews and nieces',
  'Супруги двоюродных': 'Cousins’ spouses',
  'Двоюродные дяди и тёти': 'First cousins once removed (older)',
  'Двоюродные племянники': 'First cousins once removed (younger)',
  'Четвероюродные': 'Third cousins',
  'Пятиюродные': 'Fourth cousins',
  'Двоюродные супруга': 'Spouse’s cousins',
  'Сваты': 'Children’s in-laws',
  'прапрадедушка': 'great-great-grandfather',
  'прапрабабушка': 'great-great-grandmother',
  'праправнук': 'great-great-grandson',
  'праправнучка': 'great-great-granddaughter',
  'двоюродный прадедушка': 'great-great-uncle',
  'двоюродная прабабушка': 'great-great-aunt',
  'внучатый племянник': 'grand-nephew',
  'внучатая племянница': 'grand-niece',
  'двоюродный дядя': 'first cousin once removed (older)',
  'двоюродная тётя': 'first cousin once removed (older)',
  'двоюродный племянник': 'first cousin once removed (younger)',
  'двоюродная племянница': 'first cousin once removed (younger)',
  'четвероюродный брат': 'third cousin',
  'четвероюродная сестра': 'third cousin',
  'пятиюродный брат': 'fourth cousin',
  'пятиюродная сестра': 'fourth cousin',
  'единокровный/единоутробный брат': 'half-brother',
  'единокровная/единоутробная сестра': 'half-sister',
  'полуродной брат/сестра': 'half-sibling',
  'отчим': 'stepfather',
  'мачеха': 'stepmother',
  'приёмный родитель': 'adoptive parent',
  'пасынок': 'stepson',
  'падчерица': 'stepdaughter',
  'сводный брат': 'stepbrother',
  'сводная сестра': 'stepsister',
  'близнец': 'twin',
  'близняшка': 'twin',
  'партнёр': 'partner',
  'партнёрша': 'partner',
  'партнёр(ша)': 'partner',
  'бывший муж': 'ex-husband',
  'бывшая жена': 'ex-wife',
  'бывший супруг(а)': 'ex-spouse',
  'бывший партнёр': 'ex-partner',
  'бывшая партнёрша': 'ex-partner',
  'бывший партнёр(ша)': 'ex-partner',
  'дедушка супруга': 'spouse’s grandfather',
  'бабушка супруга': 'spouse’s grandmother',
  'дедушка/бабушка супруга': 'spouse’s grandparent',
  'внук супруга': 'spouse’s grandson',
  'внучка супруга': 'spouse’s granddaughter',
  'внук/внучка супруга': 'spouse’s grandchild',
  'дядя супруга': 'spouse’s uncle',
  'дядя/тётя супруга': 'spouse’s uncle or aunt',
  'тётя супруга': 'spouse’s aunt',
  'племянник супруга': 'spouse’s nephew',
  'племянница супруга': 'spouse’s niece',
  'двоюродный брат супруга': 'spouse’s cousin',
  'двоюродная сестра супруга': 'spouse’s cousin',
  'сват': 'co-father-in-law',
  'сватья': 'co-mother-in-law',
  'дядя по браку': 'uncle by marriage',
  'тётя по браку': 'aunt by marriage',
  'супруг(а) двоюродного брата/сестры': 'cousin’s spouse',
  'супруг(а) племянника/племянницы': 'nephew’s or niece’s spouse',
  'Связь отображается отдельной строкой относительно вашей персоны.': 'The relationship is shown on a separate line relative to your person.',
  'Родственная связь': 'Relationship',
  'Настроить': 'Configure',
  'Название пространства': 'Space name',
  'Выбрать запись': 'Select entry',
  'Не удалось изменить видимость': 'Could not update visibility',
  'Видимость обновлена для {count} записей.': 'Visibility updated for {count} entries.',
  'Применяем…': 'Applying…',
  'Применить видимость': 'Apply visibility',
  'Выбрано записей: {count}': 'Selected entries: {count}',
  'Снять выделение': 'Clear selection',
  'Выбрать все': 'Select all',
  'Мои семьи': 'My families',
  'Настройка семейных веток, состава и доступа к пространствам.': 'Configure family branches, membership, and space access.',
  'Пространства семьи': 'Family spaces',
  'Выберите семейные пространства или персон': 'Choose family spaces or people',
  'Ограниченный доступ': 'Limited access',
  'Персоны, которым видно': 'People who can see',
  'Пространства и персоны': 'Spaces and people',
  'Не удалось добавить ветку': 'Could not add branch',
  'Ветка добавлена в пространство «{name}».': 'Branch added to space “{name}”.',
  'Не удалось добавить в пространство': 'Could not add to space',
  'Персона добавлена в пространство «{name}».': 'Person added to space “{name}”.',
  'Семейных пространств пока нет.': 'No family spaces yet.',
  'Пространств пока нет.': 'No spaces yet.',
  'Добавить в пространство': 'Add to space',
  'Добавить ветку': 'Add branch',
  'Ветки пока не добавлены.': 'No branches yet.',
  'Убрать ветку': 'Remove branch',
  'Ветки потомков': 'Descendant branches',
  'История на карте': 'History on the map',
  'Семья на карте': 'Family on the map',
  'Выберите пространство': 'Choose a space',
  'Все древо': 'Whole tree',
  'Приблизительно': 'Approximately',
  'Персоны': 'People',
  'Дата окончания': 'End date',
  'Дата начала': 'Start date',
  'Точная дата': 'Exact date',
  'Не удалось добавить участников': 'Could not add participants',
  'Не удалось удалить сообщение': 'Could not delete message',
  'Не удалось изменить сообщение': 'Could not edit message',
  'изменено': 'edited',
  'Удалить сообщение?': 'Delete message?',
  'Добавить участников': 'Add participants',
  'Не удалось отключить Gmail': 'Could not disconnect Gmail',
  'Не удалось запустить синхронизацию': 'Could not start sync',
  'Не удалось сохранить настройки': 'Could not save settings',
  'Не удалось подключить Gmail': 'Could not connect Gmail',
  'Последняя синхронизация: {date}': 'Last sync: {date}',
  'Открыть импортированные поездки в дневнике →': 'Open imported trips in the diary →',
  'Ошибка: {error}': 'Error: {error}',
  'Готово: проверено {scanned}, найдено {matched}, импортировано {imported}.': 'Done: scanned {scanned}, found {matched}, imported {imported}.',
  'Идёт синхронизация…': 'Sync in progress…',
  'Отключить Gmail? Импортированные записи останутся в дневнике.': 'Disconnect Gmail? Imported entries will remain in the diary.',
  'Отключить Gmail': 'Disconnect Gmail',
  'Сохранить период': 'Save period',
  'Синхронизация…': 'Syncing…',
  'PDF-билеты будут видны только пользователю, привязанному к выбранной персоне.': 'PDF tickets will only be visible to the user linked to the selected person.',
  'Искать по': 'Search to',
  'Искать с': 'Search from',
  'Персона для импорта': 'Person for import',
  'Подключить Gmail': 'Connect Gmail',
  'Поиск билетов и бронирований за выбранный период. Письма только читаются.': 'Search tickets and bookings for the selected period. Emails are read-only.',
  'Gmail и поездки': 'Gmail and trips',
  'Место работы': 'Workplace',
  'Редактирование записи': 'Edit entry',
  'уже выбран': 'already selected',
  'Начните вводить имя': 'Start typing a name',
  'Рождение ребёнка': 'Birth of a child',
  'Рождение дочери': 'Birth of a daughter',
  'Рождение сына': 'Birth of a son',
  'Мать': 'Mother',
  'Отец': 'Father',
  'По убыванию': 'Descending',
  'По возрастанию': 'Ascending',
  'Сортировка': 'Sorting',
  'Найти родственника': 'Find a relative',
  'Встреча': 'Meeting',
  'Поездка': 'Trip',
  'Открыть запись': 'Open entry',
  'Открыть фото': 'Open photo',
  'ИИ-ассистент': 'AI assistant',
  'ИИ-ассистент: расход токенов': 'AI assistant: token usage',
  'Статистика без содержимого диалогов.': 'Statistics without conversation contents.',
  'Всего токенов: {count}': 'Total tokens: {count}',
  'Участник': 'Member',
  'Запросы': 'Requests',
  'Вход': 'Input',
  'Выход': 'Output',
  'Всего': 'Total',
  'Пока нет данных о расходе токенов.': 'No token usage data yet.',
  'Открыть ИИ-ассистента': 'Open AI assistant',
  'Помогает с персонами и дневником': 'Helps with persons and diary',
  'Что можно сделать?': 'What can it do?',
  'Попросите добавить или исправить информацию о персоне или записи дневника. Любое изменение сначала появится черновиком и потребует подтверждения.':
    'Ask it to add or correct a person or diary entry. Every change first appears as a draft and requires confirmation.',
  'ИИ думает…': 'AI is thinking…',
  'Черновик изменения': 'Change draft',
  'Подтвердить': 'Confirm',
  'Изменение применено': 'Change applied',
  'Анализируем фото…': 'Analyzing photo…',
  'Анализ фото сейчас недоступен.': 'Photo analysis is currently unavailable.',
  'Возможные люди на фото': 'Possible people in the photo',
  'Лицо {index}': 'Face {index}',
  'Не этот человек': 'Not this person',
  'Отмечено на фото: {names}': 'Tagged in photo: {names}',
  'Распознанный текст': 'Recognized text',
  'Распознаём документ…': 'Recognizing document…',
  'Не удалось распознать текст.': 'Could not recognize text.',
  'Текст не найден.': 'No text found.',
  'Открыть файл': 'Open file',
  'Отменить': 'Cancel',
  'Например: добавь запись о поездке в Москву в 1985 году':
    'For example: add a diary entry about a trip to Moscow in 1985',
  'ИИ не удаляет данные и не меняет настройки':
    'AI does not delete data or change settings',
  'Прикрепить файл': 'Attach file',
  'Сделать фото': 'Take photo',
  'Удалить файл': 'Remove file',
  'Можно прикрепить не больше 5 файлов.': 'You can attach up to 5 files.',
  'Файл больше 15 МБ.': 'File is larger than 15 MB.',
  'Не удалось сделать фото.': 'Could not take a photo.',
  'Загрузка файлов…': 'Uploading files…',
  'Голосовой ввод': 'Voice input',
  'Остановить запись': 'Stop recording',
  'Слушаю…': 'Listening…',
  'Микрофон недоступен': 'Microphone is unavailable',
  'Распознавание недоступно': 'Speech recognition is unavailable',
  'Остановить': 'Stop',
  'Отправка…': 'Sending…',
  'Обновляю данные…': 'Updating data…',
  'Пустой ответ': 'Empty response',
  'Ошибка ИИ': 'AI error',
  'Не удалось применить': 'Could not apply',
  'Не удалось отменить': 'Could not cancel',
  'Увеличить': 'Zoom in',
  'Уменьшить': 'Zoom out',
  'Запись дневника': 'Diary entry',
  'Создана': 'Created',
  'Последнее изменение': 'Last modified',
  'Не изменялась': 'Not modified',
  'Координаты': 'Coordinates',
  'Место на карте': 'Location on map',
  'У выбранного родственника пока нет записей с координатами.': 'The selected relative has no entries with coordinates yet.',
  'Места из дневника': 'Places from the diary',
  'Последняя отметка в дневнике': 'Last diary location',
  'Пока нет персон с координатами. Укажите место жительства в карточке персоны или добавьте запись с геолокацией в дневник.': 'No people with coordinates yet. Set a residence in the person profile or add a diary entry with a location.',
  'На карте: {count} персон с местоположением.': 'On the map: {count} people with a location.',
  'Выберите родственника.': 'Choose a relative.',
  'Мест из дневника: {count}': 'Places from diary: {count}',
  'Родственник': 'Relative',
  'Только живые': 'Living only',
  'История мест': 'Places history',
  'Текущее местоположение': 'Current location',
  'Карта': 'Map',
  'Место не найдено на карте.': 'Place not found on the map.',
  'Город': 'City',
  'Страна': 'Country',
  'Начните вводить имя, запись дневника или дату.': 'Start typing a name, diary entry, or date.',
  'Имя, запись дневника, дата рождения…': 'Name, diary entry, birth date…',
  'Имя, родственник, запись дневника, дата…': 'Name, relative, diary entry, date…',
  'Начните вводить имя, родственника, запись дневника или дату.':
    'Start typing a name, relative, diary entry or date.',
  'Родственники': 'Relatives',
  'Умер': 'Deceased',
  'Ближайших дней рождения нет.': 'No upcoming birthdays.',
  'Ближайшие дни рождения': 'Upcoming birthdays',
  'Записи дневника': 'Diary entries',
  'Запись сохранена на устройстве и будет отправлена позже.': 'Entry saved on device and will be sent later.',
  'Удаление записи требует подключения к сети.': 'Deleting an entry requires a network connection.',
  'Изменение записи требует подключения к сети.': 'Editing an entry requires a network connection.',
  'Не удалось обновить запись': 'Could not update entry',
  'Не удалось добавить запись': 'Could not add entry',
  'Не удалось удалить запись': 'Could not delete entry',
  'Запись удалена.': 'Entry deleted.',
  'Удалить запись «{type}»?': 'Delete entry “{type}”?',
  'Удалить выбранные': 'Delete selected',
  'Удалить выбранные записи ({count})?':
    'Delete selected entries ({count})?',
  'Удалено записей: {count}.': 'Deleted entries: {count}.',
  'Не удалось удалить записи': 'Could not delete entries',
  'Удаляем…': 'Deleting…',
  'Запись обновлена.': 'Entry updated.',
  'Добавить запись': 'Add entry',
  'Записей пока нет.': 'No entries yet.',
  'Записей о персонах пока нет.': 'No entries about people yet.',
  'Записей обо мне и созданных мной пока нет.': 'No entries about me or created by me yet.',
  'Некорректная ссылка': 'Invalid link',
  'Ссылка должна начинаться с http:// или https://': 'The link must start with http:// or https://',
  'Добавить ссылку': 'Add link',
  'Ссылки': 'Links',
  'Семейные истории и факты': 'Family stories and facts',
  'Тип записи': 'Entry type',
  'Персона': 'Person',
  'Выбранная персона': 'Selected person',
  'Пол': 'Gender',
  'Автор': 'Author',
  'Не указан': 'Not specified',
  'Жив(а)': 'Living',
  'Текущий адрес': 'Current address',
  'Контактная информация': 'Contact information',
  'Контакты': 'Contacts',
  'Телефон': 'Phone',
  'Электронная почта': 'Email',
  'Страна проживания': 'Country of residence',
  'Город проживания': 'City of residence',
  'Показать близких': 'Show close relatives',
  'Открыть профиль': 'Open profile',
  'Брак': 'Marriage',
  'Развод': 'Divorce',
  'Профессия': 'Occupation',
  'Место жительства': 'Residence',
  'Образование': 'Education',
  'Погребение': 'Burial',
  'Другое': 'Other',
  'Дедушки и бабушки': 'Grandparents',
  'Внуки': 'Grandchildren',
  'Дяди': 'Uncles',
  'Тёти': 'Aunts',
  'Племянники': 'Nephews and nieces',
  'Двоюродные': 'Cousins',
  'Значение (например, профессия)': 'Value (e.g. occupation)',
  'Не удалось добавить факт': 'Could not add fact',
  'Не удалось сохранить профиль': 'Could not save profile',
  'Факт сохранён на устройстве и будет отправлен позже.':
    'Fact saved on device and will be sent later.',
  'Изменения сохранены на устройстве и будут отправлены позже.':
    'Changes saved on device and will be sent later.',
  'Пригласить': 'Invite',
  'Ссылка-приглашение создана и скопирована:':
    'Invite link created and copied:',
  'Ссылка для регистрации:': 'Registration link:',
  'Не удалось создать приглашение': 'Could not create invitation',
  'Семейные пространства': 'Family spaces',
  'Пространства ещё не созданы.': 'No spaces created yet.',

  // Chats
  'Семейный мессенджер': 'Family messenger',
  'Новый чат': 'New chat',
  'Создать чат': 'Create chat',
  'Групповой': 'Group',
  'Личный': 'Direct',
  'Название чата (необязательно)': 'Chat name (optional)',
  'Участники': 'Members',
  'Других участников семьи пока нет.': 'No other family members yet.',
  'Выберите одного собеседника': 'Select one person',
  'Чатов пока нет. Создайте первый семейный чат.':
    'No chats yet. Create the first family chat.',
  'Сообщений пока нет': 'No messages yet',
  'Чат': 'Chat',
  'участников': 'members',
  'Загрузка сообщений…': 'Loading messages…',
  'Назад к чатам': 'Back to chats',
  'Сообщение…': 'Message…',
  'Вы': 'You',
  'Сообщение сохранено и будет отправлено при подключении.':
    'Message saved and will be sent when online.',
  'Не удалось отправить сообщение': 'Could not send message',
  'Не удалось открыть чат': 'Could not open chat',
  'Удалить чат': 'Delete chat',
  'Удалить чат? Сообщения будут удалены для всех участников.':
    'Delete the chat? Messages will be deleted for all members.',
  'Не удалось удалить чат': 'Could not delete chat',
  'Только создатель чата или администратор может удалить чат':
    'Only the chat creator or a family admin can delete the chat',
  'У этой персоны нет связанного аккаунта.':
    'This person has no linked account.',
  'Вложение': 'Attachment',

  // Diary
  'Семейные истории': 'Family stories',
  'Новая запись': 'New entry',
  'Заголовок (необязательно)': 'Title (optional)',
  'Событие, факт, воспоминание…': 'Event, fact, memory…',
  'Видимость': 'Visibility',
  'Вся семья': 'Whole family',
  'Только я': 'Only me',
  'Отметить персон': 'Tag people',
  'Опубликовать': 'Publish',
  'Комментарий': 'Comment',
  'Комментарии': 'Comments',
  'Записей пока нет. Начните с первой семейной истории.':
    'No entries yet. Start with the first family story.',
  'Запись сохранена на устройстве и будет опубликована позже.':
    'Entry saved on device and will be published later.',
  'Не удалось сохранить запись': 'Could not save entry',
  'Загрузка записей…': 'Loading entries…',
  'Сегодня': 'Today',

  // Events
  'Семейный календарь': 'Family calendar',
  'Новое событие': 'New event',
  'Создать событие': 'Create event',
  'Иду': 'Going',
  'Может быть': 'Maybe',
  'Не смогу': 'Can’t go',
  'Приглашён': 'Invited',
  'Участников: {count}': 'Members: {count}',
  'Событий пока нет.': 'No events yet.',
  'Событие сохранено на устройстве и будет создано позже.':
    'Event saved on device and will be created later.',
  'Не удалось создать событие': 'Could not create event',
  'Загрузка событий…': 'Loading events…',

  // Notifications
  'Центр событий': 'Activity center',
  'Включить push': 'Enable push',
  'Включить push-уведомления?': 'Enable push notifications?',
  'Получайте уведомления о новых записях, сообщениях и событиях семьи.':
    'Get notified about new diary entries, messages and family events.',
  'Позже': 'Later',
  'Включаем…': 'Enabling…',
  'Включить': 'Enable',
  'Прочитать все': 'Mark all read',
  'Уведомлений нет.': 'No notifications.',
  'Разрешение на уведомления не выдано.':
    'Notification permission was not granted.',
  'Этот браузер не поддерживает Web Push.': 'This browser does not support Web Push.',
  'Web Push не настроен на сервере.': 'Web Push is not configured on the server.',
  'Push-уведомления включены.': 'Push notifications enabled.',
  'Push зарегистрирован. Отправка токена на сервер будет добавлена на этапе native-интеграции.':
    'Push registered. Sending the token to the server will be added in the native integration stage.',
  'Обновить данные сессии': 'Refresh session data',

  // Account
  'Аккаунт': 'Account',
  'Выйти': 'Sign out',
  'Связанная персона: {name}': 'Linked person: {name}',
  'Аккаунт пока не связан с персоной в древе.':
    'The account is not linked to a person in the tree yet.',
  'Язык': 'Language',
  'Часовой пояс': 'Time zone',
  'Email-уведомления': 'Email notifications',
  'Получать на email уведомления о новых участниках и сообщениях в чатах.':
    'Receive email notifications about new members and chat messages.',
  'Сохранить профиль': 'Save profile',
  'Профиль сохранён.': 'Profile saved.',
  'Сменить пароль': 'Change password',
  'Текущий пароль': 'Current password',
  'Новый пароль (минимум 8 символов)': 'New password (at least 8 characters)',
  'Изменить пароль': 'Change password',
  'Пароль изменён. Войдите с новым паролем.':
    'Password changed. Sign in with the new password.',
  'Не удалось изменить пароль': 'Could not change password',
  'Забыли пароль?': 'Forgot password?',
  'Восстановление пароля': 'Password recovery',
  'Введите email или логин, указанный при регистрации.':
    'Enter the email or login used during registration.',
  'Если у аккаунта указан email, ссылка для сброса пароля отправлена. Проверьте почту.':
    'If the account has an email, a password reset link has been sent. Check your inbox.',
  'Отправить ссылку': 'Send link',
  'Если email не указан, попросите администратора семьи создать ссылку для сброса пароля.':
    'If no email is set, ask the family administrator to create a password reset link.',
  'Вернуться ко входу': 'Back to sign in',
  'Ссылка для сброса пароля недействительна': 'Password reset link is invalid',
  'Ссылка для сброса пароля недействительна или истекла':
    'Password reset link is invalid or expired',
  'Пароль должен содержать минимум 8 символов':
    'Password must contain at least 8 characters',
  'Пароли не совпадают': 'Passwords do not match',
  'Придумайте новый пароль для входа в аккаунт.':
    'Create a new password for your account.',
  'Повторите пароль': 'Repeat password',
  'Сохранить пароль': 'Save password',
  'Не удалось отправить запрос': 'Could not send request',
  'Сбросить пароль': 'Reset password',
  'Ссылка для сброса отправлена на email участника.':
    'Password reset link sent to the member email.',
  'Ссылка для сброса создана и скопирована. Передайте её участнику.':
    'Password reset link created and copied. Share it with the member.',
  'Не удалось сбросить пароль': 'Could not reset password',
  'Ссылка для сброса пароля:': 'Password reset link:',
  'Не удалось удалить семью': 'Could not delete family',
  'Не удалось удалить аккаунт': 'Could not delete account',
  'Сессии': 'Sessions',
  'Отозвать': 'Revoke',
  'Завершить все': 'End all',
  'Завершить все сессии? Придётся войти заново.':
    'End all sessions? You will have to sign in again.',
  'Активных сессий нет.': 'No active sessions.',
  'Неизвестное устройство': 'Unknown device',
  'Приложение': 'App',
  'Браузер': 'Browser',
  'После первой синхронизации древо, чаты и дневник доступны без сети. Изменения отправятся автоматически.':
    'After the first sync, the tree, chats and diary are available offline. Changes are sent automatically.',

  // Spaces
  'Группы внутри одного древа. Связи и персоны остаются общими.':
    'Groups within a single tree. Relationships and people stay shared.',
  'Новое пространство': 'New space',
  'Название, например «Ветка Паков»': 'Name, e.g. “The Pak branch”',
  'Описание (необязательно)': 'Description (optional)',
  'Создать пространство': 'Create space',
  'Не удалось создать пространство': 'Could not create space',
  'Пространств пока нет. Создайте первое и наполните его персонами.':
    'No spaces yet. Create the first one and add people to it.',
  'Удалить пространство «{name}»? Персоны и связи останутся в древе.':
    'Delete the space “{name}”? People and relationships stay in the tree.',
  'Персоны в пространстве': 'People in the space',
  'Пока пусто.': 'Empty for now.',
  'Добавить персон': 'Add people',
  'Поиск по имени': 'Search by name',
  'Никого не нашли.': 'Nobody found.',
  'Убрать из пространства': 'Remove from space',
  '{count} персон': '{count} people',

  // Offline / errors
  'Страница не найдена': 'Page not found',
  'Приложение работает офлайн. Ранее загруженные данные доступны на устройстве, а новые изменения отправятся при подключении.':
    'The app works offline. Previously loaded data is available on the device, and new changes will be sent when online.',
  'Вернуться в приложение': 'Back to app',
  'Сессия истекла': 'Session expired',
  'Не удалось открыть профиль. Возможно, он ещё не загружен на устройство.':
    'Could not open the profile. It may not be loaded on this device yet.',
  'Нет сети — изменение сохранено и будет отправлено позже':
    'No connection — the change is saved and will be sent later',
  'Сервер не вернул токены для мобильного клиента':
    'The server did not return tokens for the mobile client',
  'Ошибка': 'Error',
  'Ошибка связи': 'Relationship error',
  'Ошибка импорта': 'Import error',
  'Ошибка приглашения': 'Invitation error',
  'Ошибка добавления персоны': 'Error adding person',
  'Ошибка создания семьи': 'Error creating family',
  '{count} новых': '{count} new',
  '{count} участников': '{count} members',
  '{persons} персон · {relationships} связей':
    '{persons} people · {relationships} relationships',
  'Аккаунт не привязан к персоне.': 'Account is not linked to a person.',
  'В семье пока нет участников.': 'The family has no members yet.',
  'Добавлено персон: {persons}, связей: {relationships}, фактов: {facts}, пропущено: {skipped}':
    'People added: {persons}, relationships: {relationships}, facts: {facts}, skipped: {skipped}',
  'Загрузка древа…': 'Loading tree…',
  'Загрузка профиля…': 'Loading profile…',
  'Загрузка чатов…': 'Loading chats…',
  'Не привязывать': 'Do not link',
  'Не удалось обновить привязку': 'Could not update the link',
  'Не удалось создать чат': 'Could not create chat',
  'Персона не привязана': 'No linked person',
  'Приватное': 'Private',
  'Приватное пространство видят только его автор и администраторы семьи.':
    'A private space is visible only to its creator and family admins.',
  'Привязана персона: {name}': 'Linked person: {name}',
  'Привязка аккаунта к персоне определяет, от чьего имени пользователь действует в древе и чатах.':
    'The account-to-person link determines whose name the user acts under in the tree and chats.',
  'Привязка обновлена.': 'Link updated.',
  'Публичное': 'Public',
  'Публичное пространство видят все участники семьи.':
    'A public space is visible to all family members.',
  'Роль: {role}': 'Role: {role}',
  'Русский': 'Russian',
  'Связанная персона': 'Linked person',
  'Сделать приватным': 'Make private',
  'Сделать публичным': 'Make public',
  'Удалить «{name}»? Вместе с ней удалятся все её связи, факты и отметки.':
    'Delete “{name}”? All their relationships, facts and mentions will also be deleted.',
  'Удалить персону «{name}»? Вместе с ней удалятся все её связи, факты и отметки.':
    'Delete the person “{name}”? All their relationships, facts and mentions will also be deleted.',
  'Управлять привязкой аккаунтов могут только администраторы семьи.':
    'Only family admins can manage account links.',
  'Участники семьи': 'Family members',
  'Участников': 'Members',
  'занято': 'taken',
  'персону': 'person',
  'роль': 'role',
  'это вы': 'you',
  'Изменение факта требует подключения к сети.':
    'Editing a fact requires an internet connection.',
  'Изменить': 'Edit',
  'Не удалось обновить факт': 'Could not update fact',
  'Не удалось удалить факт': 'Could not delete fact',
  'Удаление факта требует подключения к сети.':
    'Deleting a fact requires an internet connection.',
  'Удалить факт «{type}»?': 'Delete the “{type}” fact?',
  'Факт обновлён.': 'Fact updated.',
  'Факт удалён.': 'Fact deleted.',
  'Выберите персону.': 'Select a person.',
  'Выбрать существующую': 'Choose existing',
  'Для добавления родственника нужно подключение к сети.':
    'An internet connection is required to add a relative.',
  'Добавить мужа/жену': 'Add spouse',
  'Добавить ребёнка': 'Add child',
  'Добавить родителя': 'Add parent',
  'Добавить брата/сестру': 'Add sibling',
  'Сначала добавьте родителя, чтобы добавить брата или сестру.':
    'Add a parent first to add a sibling.',
  'Закрыть': 'Close',
  'Не удалось добавить родственника': 'Could not add relative',
  'Новая персона': 'New person',
  'Сохраняем…': 'Saving…',
  'Укажите имя.': 'Enter a first name.',
  'Аватар обновлён.': 'Avatar updated.',
  'Доступно обновление приложения': 'App update available',
  'Загрузка обновления… Установка начнётся автоматически.':
    'Downloading update… Installation will start automatically.',
  'Разрешите установку из этого приложения и нажмите «Обновить» снова.':
    'Allow installation from this app and tap Update again.',
  'Не удалось загрузить обновление. Попробуйте ещё раз.':
    'Could not download the update. Try again.',
  'Версия {version}': 'Version {version}',
  'Обновить до {version}': 'Update to {version}',
  'Установить аватар': 'Set avatar',
  'Изменить аватар': 'Change avatar',
  'Добавить фото': 'Add photo',
  'Загрузка фотографии требует подключения к сети.':
    'Uploading a photo requires an internet connection.',
  'Не удалось добавить фотографию': 'Could not add photo',
  'Не удалось обновить аватар': 'Could not update avatar',
  'Не удалось удалить фотографию': 'Could not delete photo',
  'Сделать аватаром': 'Set as avatar',
  'Удалить фотографию из профиля?': 'Delete this photo from the profile?',
  'Удалить вложение': 'Remove attachment',
  'Фотографии': 'Photos',
  'Фотографий пока нет.': 'No photos yet.',
  'Фотоальбом': 'Photo album',
  'Фотографии из дневника': 'Diary photos',
  'Фотографий из дневника пока нет.': 'No diary photos yet.',
  'Сначала новые': 'Newest first',
  'Сначала старые': 'Oldest first',
  'Предыдущее фото': 'Previous photo',
  'Следующее фото': 'Next photo',
  'Сортировка фотографий': 'Photo sorting',
  'Событие': 'Event',
  'Место не указано': 'Place not specified',
  'Не удалось открыть фото': 'Could not open photo',
  'Загрузка фото…': 'Loading photo…',
  'Фотография добавлена.': 'Photo added.',
  'Добавлено фотографий: {count}.': 'Photos added: {count}.',
  'Добавлено фотографий: {added}. Не удалось загрузить: {failed}.':
    'Photos added: {added}. Failed to upload: {failed}.',
  'Фотография удалена.': 'Photo deleted.',
  'Адрес не найден на карте.': 'Address not found on the map.',
  'Город, страна или адрес': 'City, country or address',
  'Долгота': 'Longitude',
  'Карта родственников': 'Relatives map',
  'Координаты определены.': 'Coordinates found.',
  'Люди на карте': 'People on the map',
  'На карте: {count} персон с указанным местом жительства.':
    'On the map: {count} people with a current residence.',
  'Найти на карте': 'Find on map',
  'Не удалось определить координаты.': 'Could not determine coordinates.',
  'Пока нет персон с координатами. Укажите место жительства в карточке персоны и нажмите «Найти на карте».':
    'No people with coordinates yet. Set a current residence in a person profile and click “Find on map”.',
  'Показать на карте': 'Show on map',
  'Текущее место жительства': 'Current residence',
  'Широта': 'Latitude',
  'Геолокация не поддерживается этим устройством.':
    'Geolocation is not supported on this device.',
  'Нажмите на карту, чтобы указать координаты.':
    'Click the map to set coordinates.',
  'Перемещайте карту, чтобы указать координаты.':
    'Move the map to set coordinates.',
  'Не удалось определить текущие координаты.':
    'Could not determine the current coordinates.',
  'Разрешение на геолокацию не выдано.':
    'Location permission was not granted.',
  'Текущая геолокация': 'Current location',
  'Текущие координаты определены.': 'Current coordinates set.',
  'Вес': 'Weight',
  'Вес, кг': 'Weight, kg',
  'Группа крови': 'Blood type',
  'Группа крови не указана': 'Blood type not specified',
  'Медицинская информация': 'Medical information',
  'Жив': 'Living',
  'Заметки': 'Notes',
  'Email персоны': 'Person email',
  'Адрес': 'Address',
  'Источник': 'Source',
  'Тип связи': 'Relationship type',
  'Связана с': 'Related to',
  'Изменение персоны': 'Person change',
  'Изменение записи': 'Entry change',
  'Новая связь': 'New relationship',
  'Работа': 'Work',
  'Родитель — ребёнок': 'Parent — child',
  'Медицинская информация: аллергии, хронические заболевания, лекарства':
    'Medical information: allergies, chronic conditions, medications',
  'Рост': 'Height',
  'Рост, см': 'Height, cm',
  'Физические данные': 'Physical details',
  'Цвет волос': 'Hair color',
  'Цвет глаз': 'Eye color',
  'кг': 'kg',
  'см': 'cm',
  'Начните вводить имя, факт, дату или название события.':
    'Start typing a name, fact, date or event title.',
  'Глобальный поиск': 'Global search',
  'Имя, факт, дата рождения или событие…':
    'Name, fact, birth date or event…',
  'Ищем…': 'Searching…',
  'Люди': 'People',
  'Найти': 'Search',
  'По запросу «{query}» ничего не найдено.':
    'Nothing found for “{query}”.',
  'Поиск по семье': 'Search the family',
  'Поиск по семье…': 'Search the family…',
  'Все результаты': 'All results',
  'Ничего не найдено': 'Nothing found',
  'Год': 'Year',
  'Дата не указана': 'Date not specified',
  'День не указан': 'Day not specified',
  'Диапазон': 'Range',
  'До': 'Before',
  'Исходная запись даты': 'Original date text',
  'Месяц не указан': 'Month not specified',
  'Около': 'About',
  'Очистить': 'Clear',
  'После': 'After',
  'Точно': 'Exact',
  'Выше': 'Up',
  'Добавить часть имени': 'Add name part',
  'Ниже': 'Down',
  'По умолчанию: Фамилия, Имя, Отчество': 'Default: Last name, First name, Middle name',
  'Порядок отображаемого имени': 'Display name order',
  'Предпросмотр: {name}': 'Preview: {name}',
  'Убрать': 'Remove',
  'Аккаунт будет отключён, сессии и уведомления удалены. Персона и общие семейные материалы останутся в древе и истории семьи.':
    'The account will be disabled, sessions and notifications removed. The person and shared family content will remain in the tree and family history.',
  'Аккаунт удалён.': 'Account deleted.',
  'Введите УДАЛИТЬ для подтверждения': 'Type DELETE to confirm',
  'Введите УДАЛИТЬ для подтверждения.': 'Type DELETE to confirm.',
  'Удаление аккаунта': 'Delete account',
  'Удалить аккаунт': 'Delete account',
  'Введите название семьи для подтверждения':
    'Type the family name to confirm',
  'Название семьи не совпадает.': 'Family name does not match.',
  'Семья': 'Family',
  'Скачать резервную копию (JSON)': 'Download backup (JSON)',
  'Удаление семьи': 'Delete family',
  'Удалить семью': 'Delete family',
  'Удалить семью навсегда': 'Delete family permanently',
  'будет удалена безвозвратно вместе с древом, персонами, связями, чатами, дневником, событиями и медиа. Это действие нельзя отменить.':
    'will be permanently deleted together with the tree, people, relationships, chats, diary, events and media. This cannot be undone.',
  'Факты о персонах': 'Facts about people',
  'Фактов о персонах пока нет.': 'No facts about people yet.',
  'Все': 'All',
  'Мои': 'Mine',
  'Фактов обо мне и созданных мной пока нет.':
    'No facts about me or created by me yet.',
  'Импорт доступен редакторам и администраторам семьи. Экспорт доступен всем участникам.':
    'Import is available to family editors and admins. Export is available to all members.',
  'Найти персону…': 'Find a person…',
  'Никого не найдено': 'Nobody found',
  'папа': 'dad',
  'мама': 'mom',
  'родитель': 'parent',
  'муж': 'husband',
  'жена': 'wife',
  'супруг(а)': 'spouse',
  'сын': 'son',
  'дочь': 'daughter',
  'ребёнок': 'child',
  'брат': 'brother',
  'сестра': 'sister',
  'дедушка': 'grandfather',
  'бабушка': 'grandmother',
  'внук': 'grandson',
  'внучка': 'granddaughter',
  'дядя': 'uncle',
  'дядя/тётя': 'uncle/aunt',
  'тётя': 'aunt',
  'племянник': 'nephew',
  'племянница': 'niece',
  'двоюродный брат': 'cousin',
  'двоюродная сестра': 'cousin',
  'родственник': 'relative',
  'Push включены': 'Push enabled',
  'Push заблокированы в настройках': 'Push blocked in settings',
  'Push не поддерживается устройством': 'Push is not supported on this device',
  'Push-уведомления отключены.': 'Push notifications disabled.',
  'Не удалось включить push.': 'Could not enable push.',
  'Не удалось отключить push.': 'Could not disable push.',
  'Отключить push': 'Disable push',
  'Проверяем push…': 'Checking push…',
  'Разрешение на уведомления выдано': 'Notification permission granted',
  'Разрешение на уведомления не выдано': 'Notification permission not granted',
  'Не удалось загрузить часть приложения':
    'Could not load part of the application',
  'Обновить': 'Reload',
  'Попробуйте обновить страницу. Если ошибка повторяется, переустановите приложение.':
    'Try reloading the page. If the error repeats, reinstall the app.',
  'Похоже, приложение обновилось, а часть файлов ещё старая. Обновите страницу или переустановите приложение.':
    'The app has likely updated while some files are still old. Reload the page or reinstall the app.',
  'Технические детали': 'Technical details',
  'Не удалось зарегистрировать устройство для push.':
    'Could not register the device for push.',
  'Ошибка регистрации native push: {error}':
    'Native push registration error: {error}',
  'Токен передан, сервер не настроен': 'Token sent, server not configured',
  'Устройство зарегистрировано, но native push ещё не настроен на сервере.':
    'The device is registered, but native push is not configured on the server yet.',
  'Native push не настроен на сервере': 'Native push is not configured on the server',
  'Native push не настроен на сервере: добавьте FCM/APNs ключи.':
    'Native push is not configured on the server: add FCM/APNs keys.',
  'Записи и воспоминания': 'Entries and memories',
  'Опубликовано семьёй': 'Published by the family',
  'Оставить воспоминание': 'Leave a memory',
  'Пока нет опубликованных записей.': 'No published entries yet.',
  'Показать ещё': 'Show more',
  'Ваши ФИО': 'Your full name',
  'Воспоминание': 'Memory',
  'Расскажите историю…': 'Tell the story…',
  'Фотографии (до 5)': 'Photos (up to 5)',
  'Удалить фото': 'Remove photo',
  'Я согласен(на) на публикацию воспоминания и фотографий на этой странице.':
    'I agree to the publication of the memory and photos on this page.',
  'Спасибо! Воспоминание отправлено и появится после проверки.':
    'Thank you! The memory has been sent and will appear after review.',
  'Отправляем…': 'Sending…',
  'Отправить воспоминание': 'Send memory',
  'Воспоминание от {name}': 'Memory from {name}',
  'Не удалось отправить воспоминание.': 'Could not send the memory.',
  'Одна из фотографий больше 8 МБ.': 'One of the photos is larger than 8 MB.',
  'Не удалось открыть страницу': 'Could not open the page',
  'Попробуйте обновить страницу позже.': 'Try refreshing the page later.',
  'Возможно, страница ещё не опубликована или была снята с публикации.':
    'The page may not be published yet or has been unpublished.',
  'Публичная страница': 'Public page',
  'Публичный адрес': 'Public address',
  'Опубликовать страницу': 'Publish page',
  'Снять с публикации': 'Unpublish',
  'Страница опубликована': 'Page published',
  'На странице видны биография, фото и записи с видимостью «Публично».':
    'The page shows the biography, photo and entries marked “Public”.',
  'Скопировать ссылку': 'Copy link',
  'Ссылка скопирована': 'Link copied',
  'Публично': 'Public',
  'Будет видно на публичной странице': 'Will be visible on the public page',
  'Публичное фото': 'Public photo',
  'По умолчанию используется аватар': 'The avatar is used by default',
  'Воспоминания на модерации': 'Memories awaiting review',
  'Отклонить': 'Reject',
  'Воспоминание гостя': 'Guest memory',
  'Публичные записи': 'Public entries',
  'Сделать публичными': 'Make public',
  'Открыть': 'Open',
  'Публикуем…': 'Publishing…',
  'Фото {index}': 'Photo {index}',
  'У персоны пока нет фотографий — будет использован аватар.':
    'The person has no photos yet — the avatar will be used.',
  'Проверьте гостевые воспоминания перед публикацией.':
    'Review guest memories before publishing.',
  'Не удалось изменить публикацию': 'Could not update the publication',
  'Воспоминание опубликовано.': 'Memory approved.',
  'Воспоминание отклонено.': 'Memory rejected.',
  'Не удалось обновить воспоминание': 'Could not update the memory',
  'Снять страницу с публикации?': 'Unpublish this page?',
  'Страница снята с публикации.': 'The page has been unpublished.',
  'Дата (точно или приблизительно)': 'Date (exact or approximate)',
  'Например, Москва, парк Горького': 'For example, Moscow, Gorky Park',
  'Кому видно': 'Who can see it',
  'Дата и место подставлены из фото. Проверьте и при необходимости измените.':
    'The date and place were filled in from the photo. Check and edit them if needed.',
  'Дата подставлена из фото. GPS-координаты не найдены — укажите место вручную.':
    'The date was filled in from the photo. No GPS coordinates were found — please enter the place manually.',
  'Только для семьи': 'Family only',
  'Спасибо! Воспоминание отправлено. Оно будет видно только семье после проверки.':
    'Thank you! The memory has been sent. It will be visible to the family only after review.',

};

function interpolate(template: string, vars?: Record<string, string | number>) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    vars[key] !== undefined ? String(vars[key]) : `{${key}}`,
  );
}

export function translate(
  locale: Locale,
  source: string,
  vars?: Record<string, string | number>,
): string {
  const template = locale === 'en' ? (EN[source] ?? source) : source;
  return interpolate(template, vars);
}

interface LocaleState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggle: () => void;
}

export const useLocale = create<LocaleState>((set, get) => ({
  locale: detectLocale(),
  setLocale: (locale) => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // ignore storage failures
    }
    set({ locale });
  },
  toggle: () => get().setLocale(get().locale === 'ru' ? 'en' : 'ru'),
}));

export type Translator = (
  source: string,
  vars?: Record<string, string | number>,
) => string;

export function useI18n(): { locale: Locale; t: Translator; setLocale: (l: Locale) => void } {
  const locale = useLocale((state) => state.locale);
  const setLocale = useLocale((state) => state.setLocale);
  const t: Translator = (source, vars) => translate(locale, source, vars);
  return { locale, t, setLocale };
}

export function useT(): Translator {
  const locale = useLocale((state) => state.locale);
  return (source, vars) => translate(locale, source, vars);
}